import { useState, useCallback } from 'react';

/**
 * Consolidated loading and error state management for B2B components
 * Replaces 57+ instances of duplicate useState loading/error patterns
 */

export interface B2BLoadingState {
  isLoading: boolean;
  error: string | null;
  isSuccess: boolean;
}

export interface B2BLoadingStateActions {
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: boolean) => void;
  clearError: () => void;
  clearSuccess: () => void;
  reset: () => void;
  executeWithLoading: <T>(asyncFn: () => Promise<T>) => Promise<T>;
}

export interface UseB2BLoadingStateOptions {
  initialLoading?: boolean;
  initialError?: string | null;
  initialSuccess?: boolean;
}

/**
 * Main hook for loading and error state management
 */
export const useB2BLoadingState = (
  options: UseB2BLoadingStateOptions = {}
): B2BLoadingState & B2BLoadingStateActions => {
  const {
    initialLoading = false,
    initialError = null,
    initialSuccess = false
  } = options;

  const [isLoading, setIsLoading] = useState(initialLoading);
  const [error, setError] = useState<string | null>(initialError);
  const [isSuccess, setIsSuccess] = useState(initialSuccess);

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
    if (loading) {
      setError(null);
      setIsSuccess(false);
    }
  }, []);

  const setErrorState = useCallback((errorMessage: string | null) => {
    setError(errorMessage);
    setIsLoading(false);
    setIsSuccess(false);
  }, []);

  const setSuccess = useCallback((success: boolean) => {
    setIsSuccess(success);
    if (success) {
      setIsLoading(false);
      setError(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSuccess = useCallback(() => {
    setIsSuccess(false);
  }, []);

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setIsSuccess(false);
  }, []);

  const executeWithLoading = useCallback(async <T>(asyncFn: () => Promise<T>): Promise<T> => {
    try {
      setLoading(true);
      const result = await asyncFn();
      setSuccess(true);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setErrorState(errorMessage);
      throw err;
    }
  }, [setLoading, setSuccess, setErrorState]);

  return {
    // State
    isLoading,
    error,
    isSuccess,
    // Actions
    setLoading,
    setError: setErrorState,
    setSuccess,
    clearError,
    clearSuccess,
    reset,
    executeWithLoading
  };
};

/**
 * Specialized hook for form submissions
 */
export const useB2BFormLoadingState = (
  options: UseB2BLoadingStateOptions = {}
) => {
  const loadingState = useB2BLoadingState(options);
  
  const { executeWithLoading } = loadingState;
  
  const handleSubmit = useCallback(async <T>(
    submitFn: () => Promise<T>
  ): Promise<T> => {
    return executeWithLoading(submitFn);
  }, [executeWithLoading]);

  return {
    ...loadingState,
    handleSubmit
  };
};

/**
 * Specialized hook for API calls
 */
export const useB2BApiLoadingState = (
  options: UseB2BLoadingStateOptions = {}
) => {
  const loadingState = useB2BLoadingState(options);
  const { executeWithLoading } = loadingState;
  
  const callApi = useCallback(async <T>(
    apiFn: () => Promise<T>
  ): Promise<T> => {
    return executeWithLoading(apiFn);
  }, [executeWithLoading]);

  return {
    ...loadingState,
    callApi
  };
};

/**
 * Specialized hook for data fetching
 */
export const useB2BDataLoadingState = (
  options: UseB2BLoadingStateOptions = {}
) => {
  const loadingState = useB2BLoadingState(options);
  const { executeWithLoading } = loadingState;
  
  const fetchData = useCallback(async <T>(
    fetchFn: () => Promise<T>
  ): Promise<T> => {
    return executeWithLoading(fetchFn);
  }, [executeWithLoading]);

  return {
    ...loadingState,
    fetchData
  };
};

























